import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ApiFragment,
    ApiThesaurusConcept,
    BaseNestedFieldDefinition,
    DialogConfig,
    DottedKey,
    Identifier,
    ShDynamicDialogService,
    ThesaurusConceptReference,
    UserFunction,
    UserService
} from '@stylehaven/components';
import { ThesaurusService } from '../../common/services/thesaurus/thesaurus.service';
import { ThesaurusConceptDialogComponent } from '../../dialog/thesaurus-concept-dialog/thesaurus-concept-dialog.component';

@Injectable({ providedIn: 'root' })
export class ThesaurusSandbox {
    constructor(
        private thesaurusService: ThesaurusService,
        private readonly translateService: SharedLanguageService,
        private dialogService: ShDynamicDialogService,
        private readonly userService: UserService
    ) {}

    public getThesaurusConcept(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptId: string
    ): Observable<ApiThesaurusConcept> {
        return this.thesaurusService
            .getThesaurusConceptById(thesaurusDefDottedKey, thesaurusConceptId)
            .pipe(catchError(error => this.showConceptDetailError(error)));
    }

    public getThesaurusConceptReferenceLabel(conceptRef: ThesaurusConceptReference): string {
        return this.thesaurusService.getThesaurusConceptReferenceLabel(conceptRef);
    }

    public getApiThesaurusConceptLabel(concept: ApiThesaurusConcept): string {
        return this.thesaurusService.getApiThesaurusConceptLabel(concept);
    }

    public getThesaurusConceptByUri(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptUri: string
    ): Observable<ApiThesaurusConcept> {
        return this.thesaurusService.getThesaurusConceptByUri(thesaurusDefDottedKey, thesaurusConceptUri);
    }

    public openThesaurusConceptDialog(
        thesaurusConcept: ApiThesaurusConcept,
        thesaurusFieldDefinition: BaseNestedFieldDefinition,
        dialogTitle: string
    ): void {
        const dialogConfig: DialogConfig = {
            title: dialogTitle,
            data: { concept: thesaurusConcept, fieldDef: thesaurusFieldDefinition }
        };

        this.dialogService.open(ThesaurusConceptDialogComponent, dialogConfig);
    }

    public showConceptDetailError(error: Error): Observable<never> {
        const message: string = this.translateService.translate('thesaurus.error.concept-not-found');

        console.error(error);
        console.error(message);
        throw error;
    }

    public hasUserFunction(userfunction: UserFunction): Observable<boolean> {
        return this.userService.hasUserFunction(userfunction);
    }

    public translate(token: string, params?: object | undefined): string {
        return this.translateService.translate(token, params);
    }

    public submitThesaurusSuggestion(
        thesaurusDefDottedKey: string,
        fragmentIds: Identifier[],
        suggestion: string
    ): Observable<ApiFragment> {
        return this.thesaurusService.submitThesaurusSuggestion(thesaurusDefDottedKey, fragmentIds, suggestion);
    }
}
